/**
 * ArchHangarForm - Form for arch/Quonset-style hangar template
 *
 * Single-unit semi-cylindrical building with configurable span, length,
 * crown height, knee wall, arch profile, and end-wall doors.
 * Updates preview in real-time as values change.
 */

import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  archHangarParamsSchema,
  DEFAULT_ARCH_HANGAR_VALUES,
  type ArchHangarParams,
  type ArchProfile,
} from '@/lib/templates/arch-hangar/params'
import { calculateTotalHeight, getArchHeightAtX } from '@/lib/templates/arch-hangar/calculations'
import type { DoorType } from '@/lib/templates/types'
import { useBuildingEditorStore } from '@/stores/buildingEditorStore'
import { RemoveAreaButton } from '../shared/RemoveAreaButton'
import { Form } from '@/components/ui/form'
import { PricingFeaturesSection } from './PricingFeaturesSection'
import { pricingFieldsSchema, defaultPricingValues, type PricingFormFields } from '@/lib/schemas/pricing-fields'

interface ArchHangarFormProps {
  initialValues?: Partial<ArchHangarParams>
  onValuesChange?: (values: ArchHangarParams) => void
  onSave: (values: ArchHangarParams, pricing?: PricingFormFields) => void
  onCancel: () => void
  isCreating?: boolean
  isSaving?: boolean
  previewLatitude?: number
  previewLongitude?: number
  isPlaced?: boolean
  areaId?: string
  areaName?: string
  onRemoved?: () => void
  initialPricing?: Partial<PricingFormFields>
}

const ARCH_PROFILE_LABELS: Record<ArchProfile, string> = {
  semicircular: 'Semicircular',
  parabolic: 'Parabolic',
}

export function ArchHangarForm({
  initialValues,
  onValuesChange,
  onSave,
  onCancel,
  isCreating = true,
  isSaving = false,
  previewLatitude,
  previewLongitude,
  isPlaced = true,
  areaId,
  areaName,
  onRemoved,
  initialPricing,
}: ArchHangarFormProps) {
  const isInitialMount = useRef(true)

  const {
    archHangarFormValues,
        updateArchHangarFormValues,
  } = useBuildingEditorStore()

  const form = useForm<ArchHangarParams>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(archHangarParamsSchema) as unknown as Resolver<ArchHangarParams>,
    defaultValues: {
      ...DEFAULT_ARCH_HANGAR_VALUES,
      ...initialValues,
      ...(archHangarFormValues || {}),
    },
  })

  const pricingForm = useForm<PricingFormFields>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(pricingFieldsSchema) as unknown as Resolver<PricingFormFields>,
    defaultValues: { ...defaultPricingValues, ...initialPricing },
  })

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form

  const watchedValues = watch()

  // Computed values
  const totalHeight = useMemo(
    () => calculateTotalHeight(watchedValues.crown_height_ft, watchedValues.knee_wall_height_ft),
    [watchedValues.crown_height_ft, watchedValues.knee_wall_height_ft]
  )

  // Max door height at door edge (the constraining point)
  const frontDoorMaxHeight = useMemo(() => {
    if (!watchedValues.front_door.enabled) return totalHeight
    const edgeX = watchedValues.front_door.width_ft / 2
    return getArchHeightAtX(
      edgeX,
      watchedValues.span_ft,
      watchedValues.crown_height_ft,
      watchedValues.knee_wall_height_ft,
      watchedValues.arch_profile
    )
  }, [watchedValues.span_ft, watchedValues.crown_height_ft, watchedValues.knee_wall_height_ft, watchedValues.arch_profile, watchedValues.front_door.width_ft, watchedValues.front_door.enabled])

  const backDoorMaxHeight = useMemo(() => {
    if (!watchedValues.back_door.enabled) return totalHeight
    const edgeX = watchedValues.back_door.width_ft / 2
    return getArchHeightAtX(
      edgeX,
      watchedValues.span_ft,
      watchedValues.crown_height_ft,
      watchedValues.knee_wall_height_ft,
      watchedValues.arch_profile
    )
  }, [watchedValues.span_ft, watchedValues.crown_height_ft, watchedValues.knee_wall_height_ft, watchedValues.arch_profile, watchedValues.back_door.width_ft, watchedValues.back_door.enabled])

  // Sync form values to store for preview updates
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    updateArchHangarFormValues(watchedValues)
  }, [
    watchedValues.span_ft,
    watchedValues.length_ft,
    watchedValues.crown_height_ft,
    watchedValues.knee_wall_height_ft,
    watchedValues.arch_profile,
    watchedValues.arch_segments,
    watchedValues.wall_thickness_ft,
    watchedValues.floor_color,
    watchedValues.wall_color,
    watchedValues.bearing_deg,
    watchedValues.name,
    watchedValues.code,
    JSON.stringify(watchedValues.front_door),
    JSON.stringify(watchedValues.back_door),
    updateArchHangarFormValues,
  ])

  // Sync bearing from store to form when changed externally (rotation ring)
  useEffect(() => {
    const storeBearing = archHangarFormValues?.bearing_deg
    const formBearing = watchedValues.bearing_deg

    if (storeBearing !== undefined && Math.abs(storeBearing - formBearing) > 0.1) {
      setValue('bearing_deg', storeBearing)
    }
  }, [archHangarFormValues?.bearing_deg, setValue])

  // Notify parent of value changes
  useEffect(() => {
    if (onValuesChange) {
      onValuesChange(watchedValues)
    }
  }, [watchedValues, onValuesChange])

  // Position display
  const latitude = previewLatitude?.toFixed(6) || watchedValues.latitude?.toFixed(6) || '—'
  const longitude = previewLongitude?.toFixed(6) || watchedValues.longitude?.toFixed(6) || '—'

  const onSubmit = async (values: ArchHangarParams) => {
    const pricingValid = await pricingForm.trigger()
    if (!pricingValid) return

    if (previewLatitude !== undefined && previewLongitude !== undefined) {
      values.latitude = previewLatitude
      values.longitude = previewLongitude
    }
    onSave(values, pricingForm.getValues())
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full flex-1 min-h-0">
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-6">
          {/* Building Section Header */}
          <div className="bg-blue-50 dark:bg-blue-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-blue-700 dark:text-blue-300">Arch Hangar</h3>
            <p className="text-xs text-muted-foreground">Semi-cylindrical Quonset-style structure</p>
          </div>

          {/* Identity Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Identity</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" {...register('name')} placeholder="e.g., Quonset A" />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input id="code" {...register('code')} placeholder="e.g., ARC-01" />
                {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
              </div>
            </div>
          </div>

          <Separator />

          {/* Dimensions Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Dimensions (feet)</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="span_ft">Span (Width)</Label>
                <Input
                  id="span_ft"
                  type="number"
                  step="any"
                  {...register('span_ft', { valueAsNumber: true })}
                />
                <p className="text-xs text-muted-foreground">30-200 ft clear span</p>
                {errors.span_ft && <p className="text-xs text-destructive">{errors.span_ft.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="length_ft">Length (Depth)</Label>
                <Input
                  id="length_ft"
                  type="number"
                  step="any"
                  {...register('length_ft', { valueAsNumber: true })}
                />
                <p className="text-xs text-muted-foreground">30-500 ft</p>
                {errors.length_ft && <p className="text-xs text-destructive">{errors.length_ft.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="crown_height_ft">Crown Height</Label>
                <Input
                  id="crown_height_ft"
                  type="number"
                  step="any"
                  {...register('crown_height_ft', { valueAsNumber: true })}
                />
                <p className="text-xs text-muted-foreground">Apex above knee wall</p>
                {errors.crown_height_ft && <p className="text-xs text-destructive">{errors.crown_height_ft.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="knee_wall_height_ft">Knee Wall</Label>
                <Input
                  id="knee_wall_height_ft"
                  type="number"
                  step="any"
                  min={0}
                  max={15}
                  {...register('knee_wall_height_ft', { valueAsNumber: true })}
                />
                <p className="text-xs text-muted-foreground">0-15 ft vertical section</p>
                {errors.knee_wall_height_ft && <p className="text-xs text-destructive">{errors.knee_wall_height_ft.message}</p>}
              </div>
            </div>

            {/* Total height display */}
            <div className="flex justify-between text-xs text-muted-foreground px-1">
              <span>Total height:</span>
              <span className="font-medium">{totalHeight.toFixed(1)} ft</span>
            </div>

            {/* Wall Thickness */}
            <div className="space-y-2">
              <Label htmlFor="wall_thickness_ft">Shell Thickness (ft)</Label>
              <Input
                id="wall_thickness_ft"
                type="number"
                step="any"
                min={0.25}
                max={2}
                {...register('wall_thickness_ft', { valueAsNumber: true })}
              />
              <p className="text-xs text-muted-foreground">0.25-2 ft (default: 0.5 ft)</p>
              {errors.wall_thickness_ft && <p className="text-xs text-destructive">{errors.wall_thickness_ft.message}</p>}
            </div>
          </div>

          <Separator />

          {/* Arch Profile Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Arch Profile</h4>

            <div className="space-y-2">
              <Label>Profile Type</Label>
              <Select
                value={watchedValues.arch_profile}
                onValueChange={(value) => setValue('arch_profile', value as ArchProfile)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(['semicircular', 'parabolic'] as const).map((p) => (
                    <SelectItem key={p} value={p}>{ARCH_PROFILE_LABELS[p]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Curve Segments</Label>
                <span className="text-sm font-medium">{watchedValues.arch_segments}</span>
              </div>
              <Slider
                value={[watchedValues.arch_segments]}
                onValueChange={(v) => setValue('arch_segments', v[0])}
                min={12}
                max={48}
                step={2}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>12 (fast)</span>
                <span>48 (smooth)</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Front Door Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm text-muted-foreground">Front Door</h4>
              <Switch
                checked={watchedValues.front_door.enabled}
                onCheckedChange={(checked) =>
                  setValue('front_door', { ...watchedValues.front_door, enabled: checked })
                }
              />
            </div>

            {watchedValues.front_door.enabled && (
              <div className="space-y-3 pl-2 border-l-2 border-blue-200 dark:border-blue-800">
                <div className="space-y-2">
                  <Label>Door Type</Label>
                  <Select
                    value={watchedValues.front_door.type}
                    onValueChange={(value) =>
                      setValue('front_door', { ...watchedValues.front_door, type: value as DoorType })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bifold">Bi-fold</SelectItem>
                      <SelectItem value="sliding">Sliding</SelectItem>
                      <SelectItem value="hydraulic">Hydraulic</SelectItem>
                      <SelectItem value="rolling">Rolling</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>Width (ft)</Label>
                    <Input
                      type="number"
                      step="any"
                      value={watchedValues.front_door.width_ft}
                      onChange={(e) =>
                        setValue('front_door', { ...watchedValues.front_door, width_ft: parseFloat(e.target.value) || 0 })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      max {(watchedValues.span_ft - 4).toFixed(0)} ft
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Height (ft)</Label>
                    <Input
                      type="number"
                      step="any"
                      value={watchedValues.front_door.height_ft}
                      onChange={(e) =>
                        setValue('front_door', { ...watchedValues.front_door, height_ft: parseFloat(e.target.value) || 0 })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      max {frontDoorMaxHeight.toFixed(1)} ft
                    </p>
                  </div>
                </div>

                {watchedValues.front_door.height_ft > frontDoorMaxHeight && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Door height exceeds arch profile at this width. It will be clipped to the arch curve.
                  </p>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Back Door Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm text-muted-foreground">Back Door</h4>
              <Switch
                checked={watchedValues.back_door.enabled}
                onCheckedChange={(checked) =>
                  setValue('back_door', { ...watchedValues.back_door, enabled: checked })
                }
              />
            </div>

            {watchedValues.back_door.enabled && (
              <div className="space-y-3 pl-2 border-l-2 border-blue-200 dark:border-blue-800">
                <div className="space-y-2">
                  <Label>Door Type</Label>
                  <Select
                    value={watchedValues.back_door.type}
                    onValueChange={(value) =>
                      setValue('back_door', { ...watchedValues.back_door, type: value as DoorType })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bifold">Bi-fold</SelectItem>
                      <SelectItem value="sliding">Sliding</SelectItem>
                      <SelectItem value="hydraulic">Hydraulic</SelectItem>
                      <SelectItem value="rolling">Rolling</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>Width (ft)</Label>
                    <Input
                      type="number"
                      step="any"
                      value={watchedValues.back_door.width_ft}
                      onChange={(e) =>
                        setValue('back_door', { ...watchedValues.back_door, width_ft: parseFloat(e.target.value) || 0 })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      max {(watchedValues.span_ft - 4).toFixed(0)} ft
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Height (ft)</Label>
                    <Input
                      type="number"
                      step="any"
                      value={watchedValues.back_door.height_ft}
                      onChange={(e) =>
                        setValue('back_door', { ...watchedValues.back_door, height_ft: parseFloat(e.target.value) || 0 })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      max {backDoorMaxHeight.toFixed(1)} ft
                    </p>
                  </div>
                </div>

                {watchedValues.back_door.height_ft > backDoorMaxHeight && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Door height exceeds arch profile at this width. It will be clipped to the arch curve.
                  </p>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Appearance Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Appearance</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="floor_color">Floor Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="floor_color"
                    type="color"
                    {...register('floor_color')}
                    className="w-12 h-9 p-1 cursor-pointer"
                  />
                  <Input {...register('floor_color')} className="flex-1" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="wall_color">Shell Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="wall_color"
                    type="color"
                    {...register('wall_color')}
                    className="w-12 h-9 p-1 cursor-pointer"
                  />
                  <Input {...register('wall_color')} className="flex-1" />
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Position Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Position</h4>
            <p className="text-xs text-muted-foreground">Drag the building on the map to set position</p>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Latitude</Label>
                <div className="px-3 py-2 bg-muted rounded-md text-sm font-mono">{latitude}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Longitude</Label>
                <div className="px-3 py-2 bg-muted rounded-md text-sm font-mono">{longitude}</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bearing_deg">Rotation (degrees)</Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="bearing_deg"
                  type="number"
                  step="any"
                  min={0}
                  max={360}
                  {...register('bearing_deg', { valueAsNumber: true })}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground">°</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Pricing & Building Features */}
          <Form {...pricingForm}>
            <PricingFeaturesSection form={pricingForm} />
          </Form>

          {/* Remove Area - only in edit mode */}
          {!isCreating && areaId && onRemoved && (
            <RemoveAreaButton
              areaId={areaId}
              areaName={areaName || 'this area'}
              onRemoved={onRemoved}
            />
          )}
        </div>
      </ScrollArea>

      {/* Sticky footer with actions */}
      <div className="border-t p-4 flex gap-2 shrink-0">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1" disabled={isSaving}>
          Cancel
        </Button>
        <Button type="submit" className="flex-1" disabled={isSaving || !isPlaced}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            isCreating ? 'Create Building' : 'Save Changes'
          )}
        </Button>
      </div>
    </form>
  )
}
